import React, { useState, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, FileText, File, X, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { apiService } from '../../services/api';



export interface UploadedFile {
  id: string;
  file: File;
  name: string;
  size: number;
  type: string;
  status: 'uploading' | 'success' | 'error';
  progress: number;
  error?: string;
  preview?: string;
}

interface FileUploadProps {
  onFilesUploaded: (files: UploadedFile[]) => void;
  onShowNotification?: (type: 'success' | 'info' | 'warning' | 'error', title: string, message: string, details?: any) => void;
  maxFiles?: number;
  maxFileSize?: number; // in MB
  acceptedTypes?: string[];
  className?: string;
}

const FileUpload: React.FC<FileUploadProps> = ({
  onFilesUploaded,
  onShowNotification,
  maxFiles = 10,
  maxFileSize = 50, // 50MB
  acceptedTypes = ['.pdf', '.docx', '.doc', '.txt'],
  className
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);

  const fileInputRef = useRef<HTMLInputElement>(null);



  const validateFile = (file: File): string | null => {
    // Check file size
    if (file.size > maxFileSize * 1024 * 1024) {
      return `File size must be less than ${maxFileSize}MB`;
    }

    // Check file type
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!acceptedTypes.includes(fileExtension)) {
      return `File type not supported. Accepted types: ${acceptedTypes.join(', ')}`;
    }

    return null;
  };

  const processFiles = useCallback((files: FileList | File[]) => {
    const newFiles: UploadedFile[] = Array.from(files).map(file => ({
      id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      status: 'uploading' as const,
      progress: 0
    }));

    // Validate files
    const validFiles: UploadedFile[] = [];
    const invalidFiles: UploadedFile[] = [];

    newFiles.forEach(file => {
      const error = validateFile(file.file);
      if (error) {
        file.status = 'error';
        file.error = error;
        invalidFiles.push(file);
      } else {
        validFiles.push(file);
      }
    });

    // Update state
    setUploadedFiles(prev => [...prev, ...validFiles, ...invalidFiles]);

    // Upload valid files to backend
    if (validFiles.length > 0) {
      validFiles.forEach(file => {
        uploadFile(file);
      });
    }

    // Call callback with all files initially
    onFilesUploaded([...validFiles, ...invalidFiles]);
  }, [maxFileSize, acceptedTypes, onFilesUploaded]);

  const uploadFile = async (file: UploadedFile) => {
    try {
      // Start with uploading status locally
      setUploadedFiles(prev => 
        prev.map(f => 
          f.id === file.id 
            ? { ...f, status: 'uploading' as const, progress: 0 }
            : f
        )
      );

      // Upload to backend
      console.log('Starting upload for:', file.name);
      const response = await apiService.uploadDocument(file.file);
      console.log('Upload response:', response);
      
      console.log('Response structure:', response);
      console.log('Response.success:', response.success);
      console.log('Response.document:', response.document);
      
      if (response.success && response.document) {
        // Check if this is a duplicate
        if (response.document.duplicate) {
          console.log('🔄 Duplicate detected, showing message...');
          
          // Show professional duplicate notification using parent function
          if (onShowNotification) {
            onShowNotification(
              'info',
              'Document Already Exists',
              `"${file.name}" has already been uploaded to the system.`,
              {
                documentId: response.document.id,
                status: response.document.status,
                chunks: response.document.chunksGenerated,
                uploadDate: new Date(response.document.uploadDate).toLocaleDateString()
              }
            );
          }
          
          // Remove the file from upload list since it's a duplicate
          setUploadedFiles(prev => prev.filter(f => f.id !== file.id));
          
          // Don't notify parent since this is a duplicate
          console.log('Duplicate file removed from upload list');
          return;
        }
        
        console.log('✅ Upload successful, updating UI...');
        
        // Update file status to success with backend ID
        console.log('🔄 Updating file status to success...');
        setUploadedFiles(prev => {
          const updatedFile: UploadedFile = { 
            ...file, 
            id: response.document?.id || file.id, // Use backend ID or fallback to original
            status: 'success', 
            progress: 100 
          };
          console.log('📝 New file state:', updatedFile);
          return prev.map(f => (f.id === file.id ? updatedFile : f));
        });
        
        // Update file with backend ID and notify parent of successful upload
        const updatedFile = { 
          ...file, 
          id: response.document?.id || file.id, // Use backend ID or fallback to original
          status: 'success' as const, 
          progress: 100 
        };
        console.log('📤 Sending updated file to parent:', updatedFile);
        onFilesUploaded([updatedFile]);
        
        console.log('Document uploaded successfully:', response.document);
      } else {
        // Update file status to error
        setUploadedFiles(prev => {
          const updatedFile: UploadedFile = { 
            ...file, 
            status: 'error', 
            error: response.error || 'Upload failed'
          };
          return prev.map(f => (f.id === file.id ? updatedFile : f));
        });
        
        // Show detailed error notification to user
        if (onShowNotification) {
          onShowNotification(
            'error',
            'Document Upload Failed',
            response.error || 'Upload failed',
            {
              fileName: file.name,
              fileSize: file.size,
              fileType: file.type
            }
          );
        }
        
        console.error('Document upload failed:', response.error);
      }
        } catch (error) {
      console.error('Upload error:', error);
      
      const errorMessage = error instanceof Error ? error.message : 'Upload failed';
      
      // Update file status to error
      setUploadedFiles(prev => {
        const updatedFile: UploadedFile = { 
          ...file, 
          status: 'error',
          error: errorMessage
        };
        return prev.map(f => (f.id === file.id ? updatedFile : f));
      });
      
      // Show error notification
      if (onShowNotification) {
        onShowNotification(
          'error',
          'Upload Error',
          errorMessage,
          {
            fileName: file.name,
            fileSize: file.size,
            fileType: file.type
          }
        );
      }
    }
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      processFiles(files);
    }
  }, [processFiles]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      processFiles(files);
    }
  }, [processFiles]);

  const removeFile = useCallback((fileId: string) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
  }, []);

  // Remove the problematic useEffect that was causing duplicates
  // useEffect(() => {
  //   console.log('FileUpload useEffect triggered, uploadedFiles:', uploadedFiles);
  //   
  //   if (uploadedFiles.length > 0) {
  //     // Create a copy of files with current progress for parent
  //     const filesWithProgress = uploadedFiles.map(file => ({
  //       ...file,
  //       // Ensure we're not mutating the original file object
  //       progress: file.progress,
  //       status: file.status
  //     }));
  //     
  //     // Only call if we have files and they're not all at 0 progress
  //     const hasProgress = filesWithProgress.some(file => file.progress > 0);
  //     console.log('Has progress:', hasProgress, 'Files:', filesWithProgress.map(f => ({ name: f.name, progress: f.progress })));
  //     
  //     if (hasProgress) {
  //       console.log('Calling onFilesUploaded with progress update');
  //       onFilesUploaded(filesWithProgress);
  //     }
  //   }
  // }, [uploadedFiles, onFilesUploaded]);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (timestamp: number): string => {
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    
    return new Date(timestamp).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  const getFileIcon = (fileName: string) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf':
        return <FileText className="w-6 h-6 text-red-500" />;
      case 'docx':
      case 'doc':
        return <FileText className="w-6 h-6 text-blue-500" />;
      case 'txt':
        return <FileText className="w-6 h-6 text-gray-500" />;
      default:
        return <File className="w-6 h-6 text-gray-400" />;
    }
  };

  const getStatusIcon = (status: UploadedFile['status']) => {
    switch (status) {
      case 'uploading':
        return <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />;
      case 'success':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'error':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      default:
        return null;
    }
  };

  return (
    <div className={className}>
      {/* Premium Upload Area */}
      <div className="relative">
        {/* Background with gradient and floating elements */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-indigo-50 rounded-3xl">
          <div className="absolute top-10 left-10 w-32 h-32 bg-blue-200 rounded-full mix-blend-multiply filter blur-xl opacity-60 animate-blob"></div>
          <div className="absolute top-20 right-20 w-24 h-24 bg-purple-200 rounded-full mix-blend-multiply filter blur-xl opacity-60 animate-blob animation-delay-2000"></div>
          <div className="absolute -bottom-8 left-32 w-28 h-28 bg-pink-200 rounded-full mix-blend-multiply filter blur-xl opacity-60 animate-blob animation-delay-4000"></div>
        </div>

        {/* Upload Zone */}
        <motion.div
          className={`relative p-12 text-center rounded-3xl border-2 border-dashed transition-all duration-300 ${
            isDragOver 
              ? 'border-blue-400 bg-blue-50/80 shadow-2xl scale-105' 
              : 'border-gray-200 hover:border-blue-300 hover:shadow-xl'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          {/* Icon with animation */}
          <motion.div
            initial={{ scale: 0.8, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            transition={{ duration: 0.5, type: "spring" }}
            className="mb-6"
          >
            <div className={`inline-flex items-center justify-center w-24 h-24 rounded-full transition-all duration-300 ${
              isDragOver 
                ? 'bg-blue-100 text-blue-600 shadow-lg' 
                : 'bg-white/80 text-gray-400 shadow-md'
            }`}>
              <Upload className={`w-12 h-12 transition-all duration-300 ${
                isDragOver ? 'scale-110' : 'scale-100'
              }`} />
            </div>
          </motion.div>
          
          {/* Main Text */}
          <motion.h3 
            className="text-3xl font-bold text-gray-900 mb-3"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            {isDragOver ? 'Drop Files Here' : 'Upload Your Documents'}
          </motion.h3>
          
          <motion.p 
            className="text-lg text-gray-600 mb-8 max-w-md mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {isDragOver 
              ? 'Release to upload your files' 
              : 'Drag and drop your trust documents here, or click to browse'
            }
          </motion.p>

          {/* Browse Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
            >
              <Upload className="w-5 h-5 mr-2" />
              Browse Files
            </button>
          </motion.div>

          {/* File Info */}
          <motion.div 
            className="mt-8 text-sm text-gray-500 space-y-2"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <div className="flex items-center justify-center space-x-6">
              <div className="flex items-center space-x-2">
                <FileText className="w-4 h-4" />
                <span>Supports: {acceptedTypes.join(', ')}</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                <span>Max: {maxFileSize}MB per file</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                <span>Up to {maxFiles} files</span>
              </div>
            </div>
          </motion.div>

          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={acceptedTypes.join(',')}
            onChange={handleFileSelect}
            className="hidden"
          />
        </motion.div>
      </div>

      {/* Premium File List */}
      <AnimatePresence>
        {uploadedFiles.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -30 }}
            className="mt-12"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center justify-between mb-8"
            >
              <div>
                <h4 className="text-2xl font-bold text-gray-900">
                  Uploaded Files
                </h4>
                <p className="text-gray-600 mt-1">
                  {uploadedFiles.length} document{uploadedFiles.length !== 1 ? 's' : ''} ready for processing
                </p>
              </div>
              
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-sm text-green-600 font-medium">Processing</span>
              </div>
            </motion.div>
            
            <div className="grid gap-4">
              {uploadedFiles.map((file, index) => (
                <motion.div
                  key={`${file.name}-${file.size}`}
                  initial={{ opacity: 0, x: -30, y: 20 }}
                  animate={{ opacity: 1, x: 0, y: 0 }}
                  exit={{ opacity: 0, x: 30, y: -20 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="group relative bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden"
                >
                  {/* Status Bar */}
                  <div className={`h-1 transition-all duration-500 ${
                    file.status === 'uploading' ? 'bg-gradient-to-r from-blue-400 to-blue-600' :
                    file.status === 'success' ? 'bg-gradient-to-r from-green-400 to-green-600' :
                    'bg-gradient-to-r from-red-400 to-red-600'
                  }`} />
                  
                  <div className="p-6">
                    <div className="flex items-start justify-between">
                      {/* File Info */}
                      <div className="flex items-start space-x-4 flex-1">
                        <div className={`p-3 rounded-xl transition-all duration-300 ${
                          file.status === 'uploading' ? 'bg-blue-50 text-blue-600' :
                          file.status === 'success' ? 'bg-green-50 text-green-600' :
                          'bg-red-50 text-red-600'
                        }`}>
                          {getFileIcon(file.name)}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <h5 className="text-lg font-semibold text-gray-900 truncate group-hover:text-blue-600 transition-colors duration-200">
                            {file.name}
                          </h5>
                          
                          <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                            <span className="flex items-center space-x-1">
                              <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                              <span>{formatFileSize(file.size)}</span>
                            </span>
                            <span className="flex items-center space-x-1">
                              <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                              <span>Uploaded {formatDate(file.file.lastModified)}</span>
                            </span>
                          </div>
                          
                          {file.error && (
                            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                              <p className="text-sm text-red-700 flex items-center space-x-2">
                                <AlertCircle className="w-4 h-4" />
                                <span>{file.error}</span>
                              </p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center space-x-3 ml-4">
                        {/* Progress Section */}
                        {file.status === 'uploading' && (
                          <div className="flex items-center space-x-4">
                            <div className="text-right">
                              <div className="text-lg font-bold text-blue-600">
                                {file.progress}%
                              </div>
                              <div className="text-xs text-gray-500">Complete</div>
                            </div>
                            
                            {/* Simple Progress Bar */}
                            <div className="w-32 bg-gray-200 rounded-full h-3">
                              <motion.div
                                className="bg-gradient-to-r from-blue-500 to-blue-600 h-3 rounded-full"
                                initial={{ width: 0 }}
                                animate={{ width: `${file.progress}%` }}
                                transition={{ duration: 0.3 }}
                              />
                            </div>
                            
                            {/* Spinner */}
                            <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                          </div>
                        )}

                                                 {/* Status Icon */}
                         {file.status !== 'uploading' && (
                           <div className="flex items-center space-x-2">
                             {getStatusIcon(file.status)}
                             <span className={`text-sm font-medium ${
                               file.status === 'success' ? 'text-green-600' : 'text-red-600'
                             }`}>
                               {file.status === 'success' ? 'Ready' : 'Error'}
                             </span>
                           </div>
                         )}
                         
                         {/* Debug Info - Remove this later */}
                         <div className="text-xs text-gray-400 mt-2">
                           Status: {file.status} | Progress: {file.progress}%
                         </div>

                        {/* Remove Button */}
                        <button
                          onClick={() => removeFile(file.id)}
                          className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all duration-200 opacity-0 group-hover:opacity-100"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>


    </div>
  );
};

export default FileUpload;
